import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { academicApi } from '../../../lib/api/academic.api';

// ─── Los datos del selector de sección (/curso, Materiales, Tareas, Encuestas)
// ANTES: escuchaba TODAS las secciones del seminario y, por cada una, leía su
// curso, período, docente y programa y contaba sus inscritos. En un seminario
// con historia importada (1.027 secciones) eran ~6.500 lecturas y ~16 s antes
// de poder elegir nada, y se repetía con cualquier cambio de cualquier sección.
//
// AHORA: las secciones DEL PERÍODO elegido (una consulta), con cursos y
// programas de un mapa único (cacheado entre pantallas), los docentes por id
// (deduplicados) y los inscritos contados solo para esas secciones.
//
// Las funciones puras (orden y armado de filas) tienen test; las lecturas son
// finas a propósito.

export interface PickerTerm {
  id: string;
  name: string;
  status?: string;
  startDate?: string;
}

export interface SectionRow {
  id: string;
  courseName: string;
  termId: string;
  termName: string;
  facultyName?: string;
  enrolledCount: number;
  /** DE QUÉ PROGRAMA es este curso. Dos programas de la misma escalera tienen
   *  cursos casi homónimos —«Hermenéutica Bíblica I» es del Certificado, la «II»
   *  del Diplomado— y sin esto la lista los muestra iguales. Elegir el
   *  equivocado no falla: se trabaja sobre el programa que no era. */
  programName?: string;
}

/** El período que se abre por defecto: en curso → abierto → el más nuevo
 *  (los períodos vienen del más nuevo al más viejo). Sin repetidos. */
export function defaultTermCandidates(terms: readonly PickerTerm[]): string[] {
  const ordered = [
    ...terms.filter((t) => t.status === 'in_progress'),
    ...terms.filter((t) => t.status === 'open'),
    ...terms,
  ].map((t) => t.id);
  return [...new Set(ordered)];
}

export interface CatalogNames {
  courses: ReadonlyMap<string, string>;
  programs: ReadonlyMap<string, string>;
}

/** Las filas del autocompletado, con los mismos rótulos de siempre. */
export function buildSectionRows(input: {
  sections: ReadonlyArray<DocumentData & { id: string }>;
  catalog: CatalogNames;
  termNames: ReadonlyMap<string, string>;
  facultyNames: ReadonlyMap<string, string>;
  enrolled: ReadonlyMap<string, number>;
}): SectionRow[] {
  const rows = input.sections
    .filter((s) => s['status'] !== 'cancelled')
    .map((s) => {
      const courseId = (s['courseId'] as string | undefined) ?? '';
      const termId = (s['termId'] as string | undefined) ?? '';
      const facultyName = input.facultyNames.get((s['facultyId'] as string | undefined) ?? '');
      const programName = input.catalog.programs.get((s['programId'] as string | undefined) ?? '');
      return {
        id: s.id,
        courseName: input.catalog.courses.get(courseId) ?? (courseId || '—'),
        termId,
        termName: input.termNames.get(termId) ?? (termId || '—'),
        ...(facultyName ? { facultyName } : {}),
        ...(programName ? { programName } : {}),
        enrolledCount: input.enrolled.get(s.id) ?? 0,
      };
    });
  return rows.sort(
    (a, b) => a.termName.localeCompare(b.termName) || a.courseName.localeCompare(b.courseName),
  );
}

// ─── Lecturas ────────────────────────────────────────────────────────────────

const col = (tenantId: string, name: string) => collection(db, `tenants/${tenantId}/${name}`);

/** Los períodos, del más nuevo al más viejo. */
export async function fetchPickerTerms(tenantId: string): Promise<PickerTerm[]> {
  const snap = await getDocs(query(col(tenantId, 'terms'), orderBy('startDate', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PickerTerm, 'id'>) }));
}

/** Nombres de cursos y programas: pocos documentos, una vez por seminario. */
export async function fetchCatalogNames(tenantId: string): Promise<CatalogNames> {
  const [courses, programs] = await Promise.all([
    getDocs(col(tenantId, 'courses')),
    getDocs(col(tenantId, 'programs')),
  ]);
  const names = (snap: typeof courses) =>
    new Map(snap.docs.map((d) => [d.id, (d.data()['name'] as string | undefined) ?? d.id]));
  return { courses: names(courses), programs: names(programs) };
}

/** Las secciones de un período (o, para un docente, solo las suyas). */
export async function fetchTermSections(
  tenantId: string,
  termId: string,
  facultyPersonId: string | null,
): Promise<Array<DocumentData & { id: string }>> {
  const filters = [
    where('termId', '==', termId),
    ...(facultyPersonId ? [where('facultyId', '==', facultyPersonId)] : []),
  ];
  const snap = await getDocs(query(col(tenantId, 'sections'), ...filters));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Los períodos en los que un docente tiene secciones (son pocas). */
export async function fetchFacultyTermIds(
  tenantId: string,
  facultyPersonId: string,
): Promise<Set<string>> {
  const snap = await getDocs(
    query(col(tenantId, 'sections'), where('facultyId', '==', facultyPersonId)),
  );
  return new Set(
    snap.docs
      .filter((d) => d.data()['status'] !== 'cancelled')
      .map((d) => d.data()['termId'] as string),
  );
}

/** ¿El período tiene alguna sección? Una lectura. */
export async function termHasSections(tenantId: string, termId: string): Promise<boolean> {
  const snap = await getDocs(
    query(col(tenantId, 'sections'), where('termId', '==', termId), limit(1)),
  );
  return !snap.empty;
}

/** El período de una sección (para un enlace directo a /curso/:sectionId). */
export async function fetchSectionTermId(
  tenantId: string,
  sectionId: string,
): Promise<string | null> {
  const snap = await getDoc(doc(db, `tenants/${tenantId}/sections/${sectionId}`));
  return snap.exists() ? ((snap.data()['termId'] as string | undefined) ?? null) : null;
}

/** Lo que el servidor sabe decir de unas secciones sin tocar datos de
 *  alumnos: el nombre del titular y los inscritos (`getTermSectionsOverview`,
 *  PR 3A). Antes se leían las fichas de los docentes y se contaban las
 *  inscripciones desde el navegador, y para eso las reglas tenían que dejar
 *  leer `people` y `enrollments` a cualquier docente.
 *
 *  De a 100 ids, el tope del servidor. `counts: false` cuando sólo hacen
 *  falta los nombres: contar es una lectura de matrícula por sección. */
export async function fetchSectionsOverview(
  tenantId: string,
  sectionIds: readonly string[],
  opts: { counts?: boolean } = {},
): Promise<{ facultyNames: Map<string, string>; enrolled: Map<string, number> }> {
  const unique = [...new Set(sectionIds.filter(Boolean))];
  const facultyNames = new Map<string, string>();
  const enrolled = new Map<string, number>();
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 100) chunks.push(unique.slice(i, i + 100));
  const pages = await Promise.all(
    chunks.map((ids) =>
      academicApi.getTermSectionsOverview({
        tenantId,
        sectionIds: ids,
        ...(opts.counts === false ? { counts: false } : {}),
      }),
    ),
  );
  for (const { sections } of pages) {
    for (const s of sections) {
      if (s.facultyName) facultyNames.set(s.sectionId, s.facultyName);
      if (s.enrolledCount !== null) enrolled.set(s.sectionId, s.enrolledCount);
    }
  }
  return { facultyNames, enrolled };
}
